/**
 * Scheduler mode: the pure pieces the settings and callback screens share
 * (docs/plans/crm-agent-scheduler-mode-plan.md B10, B9, B12 frontend; WP-10).
 *
 *   - the study ROLE (Screening / Q&A + call / Scheduler) read from, and
 *     written as, the two config.texting_agent flags;
 *   - the 30 scheduler lines with their plain names and which ones block
 *     readiness (28 always, the clinic address line when an address resolves,
 *     the caller-number line never);
 *   - the US federal holidays the "Add US federal holidays" button offers
 *     (owner decision 10: nothing is added until an admin clicks it and saves);
 *   - the Booking hours page's deploy-window gate: closed dates and clinic
 *     addresses only while the api says it stores them (checkpoint L2);
 *   - the callback ORIGIN label: 'Booked by text' and friends follow what the
 *     lead was actually told (the server's text_confirmation), and every
 *     booking that is not the assistant's reads exactly as before.
 *
 * No imports, so it is unit-tested on its own.
 */

export type AgentRole = 'screening' | 'info_call' | 'scheduler';

export interface RoleFlags {
  scheduler_only?: boolean | null;
  info_call_only?: boolean | null;
}

/** The study's role from its config.texting_agent flags. */
export function roleOf(ta?: RoleFlags | null): AgentRole {
  if (ta?.scheduler_only === true) return 'scheduler';
  if (ta?.info_call_only === true) return 'info_call';
  return 'screening';
}

/**
 * The config.texting_agent keys a role is saved as (the server merges them and
 * keeps info_call_only on for a scheduler study).
 */
export function roleFlags(role: AgentRole): { scheduler_only: boolean; info_call_only: boolean } {
  if (role === 'scheduler') return { scheduler_only: true, info_call_only: true };
  if (role === 'info_call') return { scheduler_only: false, info_call_only: true };
  return { scheduler_only: false, info_call_only: false };
}

/**
 * The config.texting_agent keys a role CHANGE sends (merged on the server).
 * A switch between Screening and Q&A + call sends info_call_only alone,
 * exactly what the old checkbox stored; only a switch into or out of the
 * Scheduler role names scheduler_only.
 */
export function roleChangePatch(from: AgentRole, to: AgentRole): Record<string, boolean> {
  if (to === 'scheduler' || from === 'scheduler') return roleFlags(to);
  return { info_call_only: to === 'info_call' };
}

/** Does a role change involve the Scheduler role (the switch that needs readiness on a live study)? */
export const involvesScheduler = (from: AgentRole, to: AgentRole) => from !== to && (from === 'scheduler' || to === 'scheduler');

/**
 * The PUT body that saves texting_agent keys: the WHOLE stored config with the
 * change applied, as the old Q&A checkbox sent it (WP-10 review, safety M1).
 * A deploy rolls the SPA minutes before the api, and the old api REPLACES the
 * config with whatever it is sent: a body of only the changed keys would drop
 * offer_call_at_wrapup and every other key there. On the new api the server
 * merge stores the same thing either way (the row's updated_at rides along, so
 * a stale copy is refused, not written), and a null still deletes its key.
 */
export function textingAgentSaveBody(
  stored: { config?: Record<string, any> | null } | null | undefined,
  keys: Record<string, unknown>,
): { config: Record<string, any> } {
  const cfg = stored?.config && typeof stored.config === 'object' && !Array.isArray(stored.config) ? stored.config : {};
  const ta = cfg.texting_agent && typeof cfg.texting_agent === 'object' && !Array.isArray(cfg.texting_agent) ? cfg.texting_agent : {};
  return { config: { ...cfg, texting_agent: { ...ta, ...keys } } };
}

/**
 * The role choices to show. The Scheduler role is offered only once the api
 * says it supports it (the config read's `scheduler_supported`): during a
 * deploy the SPA can run against the previous api, which would store the role
 * with no readiness gate (WP-10 review, safety M1). A study already set to
 * Scheduler always shows its own role.
 */
export function visibleRoleOptions(schedulerSupported: boolean, current: AgentRole) {
  return ROLE_OPTIONS.filter((o) => o.role !== 'scheduler' || schedulerSupported || current === 'scheduler');
}

/**
 * Is the readiness on screen a PREVIEW of another role (a refused role switch
 * returns the new role's checklist) rather than the study's saved role? The
 * Scheduler's checklist carries a `scheduler` detail and no other role's does.
 * A mark made now is stamped with the SAVED role, so on a preview the page
 * offers no "Mark done" (a Scheduler study counts only marks made while it is
 * set to Scheduler).
 */
export const isRolePreview = (savedRole: AgentRole, readiness: { scheduler?: unknown } | null | undefined): boolean =>
  Boolean(readiness) && Boolean(readiness?.scheduler) !== (savedRole === 'scheduler');

export const ROLE_OPTIONS: Array<{ role: AgentRole; label: string; hint: string }> = [
  {
    role: 'screening',
    label: 'Screening',
    hint: "The agent answers questions from the brief and screens the lead with the study's questions, then offers the questionnaire or a call.",
  },
  {
    role: 'info_call',
    label: 'Q&A + call',
    hint: 'The agent only answers questions about the study and sets up a call with the team (it sends the scheduling link). No prescreening or eligibility questions, no online form, and the study needs no screener to go live.',
  },
  {
    role: 'scheduler',
    label: 'Scheduler',
    hint: "The assistant only books the call. It texts eligible and needs-review leads right away, asks for a day and time, and books it on the study team's CRM calendars. It never discusses the study, and it sends no links unless the lead has trouble or asks.",
  },
];

/** The 30 scheduler lines, in the order the settings page lists them, with plain names. */
export const SCHEDULER_LINES: Array<{ line: string; label: string }> = [
  { line: 'welcome', label: 'Welcome' },
  { line: 'welcome_night', label: 'Welcome at night' },
  { line: 'intro', label: 'Intro on a continuing thread' },
  { line: 'ask_time', label: 'Ask for a day and time' },
  { line: 'booked', label: 'Booked confirmation' },
  { line: 'offer', label: 'Offer two times' },
  { line: 'offer_taken', label: 'Time taken, two others' },
  { line: 'offer_stale', label: 'Thanks for waiting, two times' },
  { line: 'out_of_hours', label: 'Outside our hours' },
  { line: 'confirm_reading', label: 'Confirm the time read' },
  { line: 'clarify', label: 'Clarify' },
  { line: 'clarify_2', label: 'Clarify again' },
  { line: 'pick_one', label: 'Pick one of two' },
  { line: 'call_now', label: 'Call me now' },
  { line: 'later', label: 'Text me later' },
  { line: 'deflect', label: 'Study question deflection' },
  { line: 'deflect_2', label: 'Second deflection' },
  { line: 'location', label: 'Clinic address' },
  { line: 'call_by_phone', label: 'The call is by phone' },
  { line: 'call_length', label: 'How long the call takes' },
  { line: 'call_number', label: 'What number we call from' },
  { line: 'disclosure', label: 'Automated assistant disclosure' },
  { line: 'rescue', label: 'Trouble link' },
  { line: 'link_on_request', label: 'Link on request' },
  { line: 'rescue_again', label: 'Link again' },
  { line: 'escalate', label: 'Hand-off to the team' },
  { line: 'change_request', label: 'Change or cancel request' },
  { line: 'goodbye', label: 'Goodbye' },
  { line: 'nudge', label: 'Follow-up' },
  { line: 'nudge_final', label: 'Last follow-up' },
];

export const schedLineKey = (line: string) => `texting_agent.template.sched_${line}`;

/**
 * Whether a line blocks readiness: 'yes', 'with_address' (the clinic address
 * line, required once an address resolves) or 'never' (the caller-number line,
 * published after the pilot's caller-ID check).
 */
export function lineRequirement(line: string, addressResolves: boolean): 'yes' | 'with_address' | 'never' {
  if (line === 'call_number') return 'never';
  if (line === 'location') return addressResolves ? 'yes' : 'with_address';
  return 'yes';
}

// ---------------------------------------------------------------------------
// US federal holidays (owner decision 10: the button only adds; the admin saves)
// ---------------------------------------------------------------------------

const pad = (n: number) => String(n).padStart(2, '0');
const key = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;

/** The nth `weekday` (0 = Sunday) of a month, or the last one when n is -1. */
function nthWeekday(year: number, month: number, weekday: number, n: number): number {
  if (n > 0) {
    const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    return 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
  }
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lastDow = new Date(Date.UTC(year, month - 1, lastDay)).getUTCDay();
  return lastDay - ((lastDow - weekday + 7) % 7);
}

/** A fixed-date holiday as OBSERVED: Saturday moves to Friday, Sunday to Monday (5 U.S.C. 6103). */
function observed(year: number, month: number, day: number): string {
  const t = new Date(Date.UTC(year, month - 1, day));
  const dow = t.getUTCDay();
  if (dow === 6) t.setUTCDate(t.getUTCDate() - 1);
  if (dow === 0) t.setUTCDate(t.getUTCDate() + 1);
  return key(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}

/** The eleven US federal holidays of a year, on the days offices close. */
export function federalHolidays(year: number): Array<{ date: string; name: string }> {
  return [
    { date: observed(year, 1, 1), name: "New Year's Day" },
    { date: key(year, 1, nthWeekday(year, 1, 1, 3)), name: 'Martin Luther King Jr. Day' },
    { date: key(year, 2, nthWeekday(year, 2, 1, 3)), name: "Washington's Birthday" },
    { date: key(year, 5, nthWeekday(year, 5, 1, -1)), name: 'Memorial Day' },
    { date: observed(year, 6, 19), name: 'Juneteenth' },
    { date: observed(year, 7, 4), name: 'Independence Day' },
    { date: key(year, 9, nthWeekday(year, 9, 1, 1)), name: 'Labor Day' },
    { date: key(year, 10, nthWeekday(year, 10, 1, 2)), name: 'Columbus Day' },
    { date: observed(year, 11, 11), name: 'Veterans Day' },
    { date: key(year, 11, nthWeekday(year, 11, 4, 4)), name: 'Thanksgiving Day' },
    { date: observed(year, 12, 25), name: 'Christmas Day' },
  ];
}

/**
 * The closed dates after pressing "Add US federal holidays": this year's and
 * next year's holidays from `todayKey` on, merged into `existing` (kept,
 * deduped, sorted). `added` lists what the button added.
 */
export function withFederalHolidays(existing: string[], todayKey: string): { dates: string[]; added: Array<{ date: string; name: string }> } {
  const year = Number(todayKey.slice(0, 4));
  const have = new Set(existing);
  const added = [...federalHolidays(year), ...federalHolidays(year + 1)]
    .filter((h) => h.date >= todayKey && !have.has(h.date));
  const dates = [...new Set([...existing, ...added.map((h) => h.date)])].sort();
  return { dates, added };
}

/** The name of a federal holiday on this date, if it is one (for the list's labels). */
export function holidayName(date: string): string | null {
  const year = Number(date.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  for (const y of [year, year + 1]) {
    const hit = federalHolidays(y).find((h) => h.date === date);
    if (hit) return hit.name;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Booking hours during a deploy (checkpoint regression L2)
// ---------------------------------------------------------------------------

/**
 * Does the api behind the Booking hours page store closed dates and clinic
 * addresses? A deploy rolls this page minutes before the api, and the previous
 * api drops `closed_dates` from a save without a word (its PUT whitelist) and
 * has no clinic-sites route (404). So both sections show only once the booking
 * read or save says `scheduler_supported: true`, the Role radio's rule; a list
 * in the read proves nothing (a rolled-back api still returns a stored one).
 */
export const bookingExtrasSupported = (data: { scheduler_supported?: unknown } | null | undefined): boolean =>
  data?.scheduler_supported === true;

/** What the Booking hours page shows in place of the two sections until then. */
export const BOOKING_EXTRAS_PENDING = 'Available once the update that adds it finishes. Reload this page in a few minutes.';

export interface BookingSaveFields {
  days: number[];
  day_start: string;
  day_end: string;
  window_days: number;
  slot_minutes: number;
  min_notice_minutes: number;
  token_ttl_days: number;
  recruiter_invite_email: boolean;
  invite_show_patient_name: boolean;
  closed_dates?: string[];
}

/**
 * The PUT /settings/booking body: the nine keys it always sent, and
 * closed_dates only once edited on an api that stores them, so a save that
 * never touched them writes exactly what it wrote before.
 */
export function bookingSaveBody(b: BookingSaveFields, closedDirty: boolean, supported: boolean): BookingSaveFields {
  return {
    days: b.days,
    day_start: b.day_start,
    day_end: b.day_end,
    window_days: b.window_days,
    slot_minutes: b.slot_minutes,
    min_notice_minutes: b.min_notice_minutes,
    token_ttl_days: b.token_ttl_days,
    recruiter_invite_email: b.recruiter_invite_email,
    invite_show_patient_name: b.invite_show_patient_name,
    ...(closedDirty && supported ? { closed_dates: b.closed_dates || [] } : {}),
  };
}

/**
 * Did a save that sent closed dates lose them? Only a reply that says it
 * supports them proves they were stored: the previous api answers 200 with the
 * dates gone, which the page must never report as saved.
 */
export const closedDatesDropped = (sentClosedDates: boolean, reply: { scheduler_supported?: unknown } | null | undefined): boolean =>
  sentClosedDates && !bookingExtrasSupported(reply);

// ---------------------------------------------------------------------------
// The callback editor: handing an assistant booking to someone else
// ---------------------------------------------------------------------------

/**
 * Should the callback editor offer "Also make <new recruiter> the lead's
 * owner" (plan B5 LATER EDITS)? Only for a call the scheduling assistant or
 * its booking page booked, as the SERVER flags it (`scheduler_booking`; never
 * booked_via, because a legacy /book call is public_link too), when the call
 * goes to a different person, and while the lead's owner is still the call's
 * current recruiter. Every other edit sends exactly what it sends today.
 */
export function offersOwnerMove(
  cb: { scheduler_booking?: boolean; assignee_user_id: string | null },
  newAssigneeId: string | null | undefined,
  leadOwnerId: string | null | undefined,
): boolean {
  return Boolean(
    cb.scheduler_booking === true
    && cb.assignee_user_id
    && newAssigneeId
    && newAssigneeId !== cb.assignee_user_id
    && (leadOwnerId ?? null) === cb.assignee_user_id,
  );
}

/**
 * A removed recruiter's call on the Recruiters tab (WP-10 review L4): where it
 * came from, from the booking's own booked_via (a scheduler PAGE booking is not
 * the assistant's text booking).
 */
export const removedCallOrigin = (bookedVia: string | null | undefined): string =>
  bookedVia === 'public_link' ? 'call booked on the scheduling page' : 'call booked by text';

/**
 * The PATCH body that hands a removed recruiter's call to `to`. The lead moves
 * with it only when the admin left "Also make <them> the lead's owner" ticked,
 * and only while the lead is still the removed recruiter's (the callback
 * editor's rule; the server re-checks). Unticked, only the call moves.
 */
export function reassignCallBody(
  call: { owner_is_assignee: boolean },
  to: string,
  alsoMoveLead: boolean,
): { assignee_user_id: string; move_owner?: true } {
  return { assignee_user_id: to, ...(call.owner_is_assignee && alsoMoveLead ? { move_owner: true as const } : {}) };
}

/**
 * The note the editor shows after saving a scheduler booking: whether the lead
 * moved with the call ('already_owner' is done, so it says nothing) and any
 * calendar warnings, which never stop a save. Null when there is nothing to say.
 */
export function callbackEditNotice(
  askedToMove: boolean,
  data: { move_owner?: { moved?: boolean; reason?: string } | null; warnings?: Array<{ message?: string }>; warnings_unavailable?: boolean } | null | undefined,
  newAssigneeName: string,
): string | null {
  const lines: string[] = [];
  const move = data?.move_owner;
  if (askedToMove && move) {
    if (move.moved === true) lines.push(`The lead is now ${newAssigneeName}'s too.`);
    else if (move.reason === 'owner_changed') lines.push('The call moved, but the lead did not: someone changed its owner in the meantime.');
    else if (move.reason === 'error') lines.push('The call moved, but moving the lead failed. Change the owner on the lead instead.');
  }
  const warnings = Array.isArray(data?.warnings) ? data!.warnings!.map((w) => w?.message).filter(Boolean) : [];
  if (warnings.length) lines.push(`Saved, but check: ${warnings.join('; ')}.`);
  if (data?.warnings_unavailable) lines.push("Saved. The recruiter's calendar could not be checked.");
  return lines.length ? lines.join(' ') : null;
}

// ---------------------------------------------------------------------------
// Where a booked call came from, and what the lead was told
// ---------------------------------------------------------------------------

export interface TextConfirmation {
  state: 'sent' | 'queued' | 'not_told' | 'none';
  quiet?: boolean;
}

export interface CallbackOriginLike {
  booked_via: string;
  scheduler_booking?: boolean;
  text_confirmation?: TextConfirmation | null;
}

/**
 * The origin line of a booked call. The assistant's bookings read 'Booked by
 * text' and follow the confirmation's REAL state: a quiet night booking whose
 * confirmation is still queued says the patient is told at 8:00 AM, and any
 * assistant or scheduler-page booking whose confirmation was stopped, failed
 * or never delivered says the patient was NOT told. Every other booking reads
 * exactly as before ('Patient picked this time' on Today, 'Patient-booked' on
 * the lead page, 'Recruiter-booked').
 */
export function callbackOrigin(cb: CallbackOriginLike, surface: 'today' | 'card'): { label: string; warn: boolean } {
  const tc = cb.text_confirmation || null;
  if (cb.booked_via === 'agent') {
    const night = tc?.quiet === true;
    if (tc?.state === 'not_told') {
      return { label: `Booked by text${night ? ' overnight' : ''}, patient NOT told: confirm by phone`, warn: true };
    }
    if (tc?.state === 'queued') {
      return { label: night ? 'Booked by text overnight, patient told at 8:00 AM' : 'Booked by text, confirmation on its way', warn: false };
    }
    return { label: 'Booked by text', warn: false };
  }
  const legacy = cb.booked_via === 'public_link'
    ? (surface === 'today' ? 'Patient picked this time' : 'Patient-booked')
    : 'Recruiter-booked';
  if (cb.booked_via === 'public_link' && cb.scheduler_booking === true && tc?.state === 'not_told') {
    return { label: `${legacy}, patient NOT told: confirm by phone`, warn: true };
  }
  return { label: legacy, warn: false };
}
